import { useEffect, useLayoutEffect, useRef, useReducer, useCallback } from "react";
import { Box } from "@/components/display/display-primitives";
import { PhotoSlide } from "./photo-slide";
import { MessageSlide } from "./message-slide";
import { WeatherSlide } from "./weather-slide";
import type { FeedItem } from "@/lib/display-feed";
import { resolveSlideDuration, type SlideTimingSettings } from "@mem/shared";

interface DisplayCarouselProps {
  items: FeedItem[];
  timingSettings?: SlideTimingSettings;
  /** Appelé quand le carrousel termine un tour complet (dernier → premier). */
  onCycleComplete?: () => void;
  /**
   * Incrémenté par la page display UNIQUEMENT quand du nouveau contenu
   * arrive dans la queue (nouveau message / photo). Déclenche un saut
   * immédiat à l'index 0 pour montrer le nouveau contenu.
   * N'est PAS incrémenté lors de la rotation des archives.
   */
  jumpKey?: number;
}

export function DisplayCarousel({ items, timingSettings, onCycleComplete, jumpKey }: DisplayCarouselProps) {
  // ── Ref‑based state ───────────────────────────────────────
  const activeIndexRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCycleCompleteRef = useRef(onCycleComplete);
  onCycleCompleteRef.current = onCycleComplete;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const timingSettingsRef = useRef(timingSettings);
  timingSettingsRef.current = timingSettings;

  const [, forceRender] = useReducer((x) => x + 1, 0);

  // ── Timer engine ──────────────────────────────────────────
  function clearTimer() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function scheduleNext(index: number) {
    clearTimer();
    const currentItems = itemsRef.current;
    const current = currentItems[index];
    if (!current) return;
    const duration = resolveSlideDuration(current, timingSettingsRef.current);
    timerRef.current = setTimeout(advance, duration);
  }

  function jumpTo(index: number) {
    clearTimer();
    const currentItems = itemsRef.current;
    if (currentItems.length === 0) {
      activeIndexRef.current = 0;
      forceRender();
      return;
    }
    activeIndexRef.current = Math.min(index, currentItems.length - 1);
    forceRender();
    scheduleNext(activeIndexRef.current);
  }

  const advance = useCallback(() => {
    const items = itemsRef.current;
    if (items.length === 0) return;
    const current = activeIndexRef.current;
    const next = (current + 1) % items.length;
    if (next === 0 && current === items.length - 1) {
      onCycleCompleteRef.current?.();
    }
    activeIndexRef.current = next;
    forceRender();
    scheduleNext(next);
  }, []);

  // ── Réagit aux changements d'items ────────────────────────
  // On ne saute PAS ici — les sauts sont pilotés exclusivement par jumpKey.
  // On clamp juste l'index si la liste a raccourci (ex : item supprimé).
  useLayoutEffect(() => {
    if (items.length === 0) {
      clearTimer();
      activeIndexRef.current = 0;
      forceRender();
      return;
    }
    if (activeIndexRef.current >= items.length) {
      jumpTo(0);
    } else {
      forceRender();
    }
  }, [items]);

  // ── Saut forcé sur jumpKey (nouveau contenu dans la queue) ─
  useLayoutEffect(() => {
    if (items.length > 0) {
      jumpTo(0);
    }
  }, [jumpKey]);

  // ── Démarrer le timer au montage ──────────────────────────
  useEffect(() => {
    if (items.length > 0) {
      scheduleNext(activeIndexRef.current);
    }
    return clearTimer;
  }, []);

  if (items.length === 0) return null;

  const activeIdx = activeIndexRef.current;

  return (
    <Box
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#000",
      }}
    >
      {items.map((item, i) => {
        const isActive = i === activeIdx;
        if (item.type === "photo") {
          return <PhotoSlide key={item.id} item={item} isActive={isActive} />;
        }
        if (item.type === "weather_card") {
          return <WeatherSlide key={item.id} item={item} isActive={isActive} />;
        }
        // message (et tout type inconnu futur)
        return <MessageSlide key={item.id} item={item} isActive={isActive} />;
      })}
    </Box>
  );
}
